import { Alert, Button, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, Stack, TextField, Typography } from "@mui/material";
import { createListStore, ListProp } from "../../common/listStore";
import { renderClose, useDialog, useSimpleDialog } from "../../common/dialog";
import { useEffect, useMemo, useState } from "react";
import { CustomerDto, newMailOrder, MailOrderEditDto, OrderProductDto, emptyMailOrder, RetentionSaveDto, MailOrderDto, OrderProjectDto } from "./types";
import { asapScheduler, BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, observeOn } from "rxjs";
import { useDefaultValues } from "../../common/defaultValues";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { DeliveryMethodId, EnumItemDto, OrderStateId, PaymentMethodId, useDeliveryMethods, useOrderStates, usePaymentMethods } from "../../common/enum";
import { getMostImportantValidationMessage, propBoolField, propDateField, propEnumField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { Add, Edit, Remove } from "@mui/icons-material";
import shallow from "zustand/shallow";
import { propCurrency, propIf, propLength, propLengthMax, propNumber, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { customerOrderDialogTitle, getOrderState, useAddressList, useCustomerBasicInfo } from "./common";
import { formatNumber } from "../../common/localization";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { UserTypeId, useAccountStore } from "../../common/account";
import { InputSizedButtonSx } from "../../common/form";
import { OrderShipmentDetailDialog } from "../../common/OrderShipmentDetailDialog";
import { dayjs } from "../../common/config";
import { DeliveryPlaceDialog } from "./DeliveryPlaceDialog";
import { keyBy, round, sortBy, sum, sumBy } from "lodash";
import { SaleArticleDto, ShipmentDetailDto, getOrderCalculation, isOrderEditable } from "../../common/order";
import { ShipmentAutoQuantity } from "./ShipmentAutoQuantity";
import { newId, useRxJsSub } from "../../common/utils";
import { ProjectMailOrderConditionDto } from "../../common/types";

export function MailOrderDialog(props: {
    orderId: number | null;
    close: () => void;
    useCustomerStore: UseEntityStore<CustomerDto>;
    retention?: RetentionSaveDto;
    clearRetention?(): void;
    newMoData?: MailOrderDto;
    setNewMoData?(data: MailOrderDto): void;
}) {
    const userTypeId = useAccountStore(p => p.data?.accessTokenDecoded.userTypeId);
    const isEditRole = userTypeId !== UserTypeId.CollectionAgency && userTypeId !== UserTypeId.View;
    const isAdminRole = userTypeId === UserTypeId.Admin;
    const customer = useCustomerBasicInfo(props.useCustomerStore);
    const def = useDefaultValues();
    const [{ useOrderStore, useProductsStore, useProjectsStore, useDetailsStore, useAutosStore }] = useState(() => {
        const useOrderStore = createEntityStore<MailOrderEditDto>({
            path: "customers/mailOrders",
            id: props.orderId,
            data: props.orderId == null ? newMailOrder(props.useCustomerStore, props.retention, props.newMoData) : emptyMailOrder(customer.id!),
            getNewId: p => p.id!,
            onSaved(data) {
                if (props.clearRetention != null) {
                    props.clearRetention();
                }
                props.useCustomerStore.getState().fetchServerData();
                useOrderStore.getState().setClientDataProp("details", data.details);
                useOrderStore.getState().setClientDataProp("autos", data.autos);
                if (useOrderStore.getState().clientData.paymentMethodId == null) {
                    useOrderStore.getState().setClientDataProp("paymentMethodId", data.paymentMethodId);
                }
                useOrderStore.getState().setClientDataProp("retention", null);
                props.close();
            },
            onFetched: () => {
                useOrderStore.getState().setClientDataProp("freePostage", useOrderStore.getState().clientData.details
                    .concat(useOrderStore.getState().clientData.autos)
                    .some(p => p.isPost && p.price === 0));
                updateOrder();
            },
            getDataForSave(data) {
                return {
                    details: sortBy(data.details.map(p => ({
                        id: p.id,
                        baseQuantity: p.baseQuantity,
                        code: p.code,
                        isPost: p.isPost,
                        name: p.name,
                        price: p.price,
                        quantity: p.quantity,
                        saleArticleId: p.saleArticleId,
                        weight: p.weight,
                        packageType: p.packageType,
                        projectMailOrderConditionId: p.projectMailOrderConditionId,
                        isAvailable: p.isAvailable,
                    })), p => p.code, p => p.price),
                    autos: sortBy(data.autos.filter(p => p.quantity > 0).map(p => ({
                        id: p.id,
                        baseQuantity: p.baseQuantity,
                        code: p.code,
                        isPost: p.isPost,
                        name: p.name,
                        price: p.price,
                        quantity: p.quantity,
                        saleArticleId: p.saleArticleId,
                        weight: p.weight,
                        packageType: p.packageType,
                        projectMailOrderConditionId: p.projectMailOrderConditionId,
                        isAvailable: p.isAvailable,
                    })), p => p.code, p => p.price),
                    paymentGatewayTransId: data.paymentGatewayTransId,
                    deliveryMethodId: data.deliveryMethodId,
                    deliveryMethodPlaceId: data.deliveryMethodPlaceId,
                    deliveryAddressId: data.deliveryAddressId,
                    confirmedByCustomer: data.confirmedByCustomer,
                    discAmt: data.discAmt,
                    minShpDte: data.minShpDte,
                    orderStateId: data.orderStateId,
                    paymentMethodId: data.paymentMethodId,
                    projectId: data.projectId,
                    sc: data.sc,
                    customerId: customer.id!,
                    copyFromOrderId: data.copyFromOrderId,
                    retention: data.retention,
                };
            },
        });
        const useProductsStore = createListStore<OrderProductDto>({
            path: `customers/mailOrders/getProductsAndProjects?orderId=${props.orderId != null ? props.orderId : props.newMoData?.id != null ? props.newMoData.id : ""}`,
        });

        const useProjectsStore = createListStore({
            data: combineLatest([
                useProductsStore.getState().items$,
                useOrderStore.getState().clientData$.pipe(map(p => ({ productId: p.data.productId, isFetching: p.isFetching })), distinctUntilChanged(shallow))
            ]).pipe(map(p => ({ items: p[0].items.find(s => s.id === p[1].productId)?.projects || [], isFetching: p[0].isFetching || p[1].isFetching })))
        });

        const detailsListProps: ListProp<ShipmentDetailDto>[] = [
            { column: "code", label: "Art", width: 70 },
            { column: "name", label: "Description", flex: 1 },
            {
                column: "quantity", label: "Qty", width: 70, type: "number", renderCell: p =>
                    <ShipmentAutoQuantity useStore={useOrderStore} auto={p.row} def={def} updateOrder={() => updateOrder()} simpleDialog={simpleDialog} />,
            },
            { column: "price", label: "Price", width: 90, type: "currency" },
            { column: "weight", label: "Weight [g]", width: 90, type: "number" },
        ];

        const useDetailsStore = createListStore<ShipmentDetailDto>({
            data: useOrderStore.getState().clientData$.pipe(map(p => ({ items: p.data.details, isFetching: p.isFetching })), distinctUntilChanged(shallow)),
            getId: item => item.saleArticleId, itemsPerPage: 5, listProps: detailsListProps
        });

        const useAutosStore = createListStore<ShipmentDetailDto>({
            data: useOrderStore.getState().clientData$.pipe(
                map(p => ({ autos: p.data.autos, orderStateId: p.data.orderStateId, paymentMethodId: p.data.paymentMethodId, isFetching: p.isFetching, isEditing: p.data.isEditing })),
                distinctUntilChanged(shallow),
                map(p => ({ items: !isOrderEditable(p.orderStateId, p.paymentMethodId, isAdminRole) || !p.isEditing ? p.autos : p.autos.filter(p => p.missingAmount === 0 || p.missingAmount == null), isFetching: p.isFetching }))
            ),
            itemsPerPage: 8, listProps: detailsListProps,
        });

        return { useOrderStore, useProductsStore, useProjectsStore, useDetailsStore, useAutosStore };
    });

    useEffect(() => {
        if (props.orderId == null && props.newMoData == null) {
            const sub = useProductsStore.getState().items$.pipe(observeOn(asapScheduler)).subscribe({
                next(products) {
                    if (products.items.length > 0) {
                        const product = products.items[0];
                        const project = product.projects[0];
                        useOrderStore.getState().setClientDataProp("productId", product.id);
                        useOrderStore.getState().setClientDataProp("projectId", project.id);
                        useOrderStore.getState().setClientDataProp("details", []);
                        sub.unsubscribe();
                    }
                }
            });
            return () => sub.unsubscribe();
        }
    }, [props.orderId, props.newMoData, useOrderStore, useProductsStore]);

    useEffect(() => {
        useOrderStore.getState().setPropValidators([
            propValidValue("minShpDte", "minShpDteFormatted"),
            propRequired("minShpDte"),
            propRequired("projectId"),
            propValidValue("discAmt", "discAmtFormatted"),
            propRequired("discAmt"),
            propCurrency("discAmt"),
            propNumber("discAmt", p => p < 0 && "Discount can't be less than 0."),
            propNumber("toPay", p => p < 0 && "Value can't be less than 0."),
            propValueValidator("details", p => p.length === 0 && "Mail order must have articles."),
            propLength("sc", 0, 4),
            propValueValidator("minShpDte", (p, e) => p != null && dayjs.tz(p).isBefore(dayjs.tz(e.ordDte)) && "Min. SHP date cannot be before Enrolment date."),
            propRequired("deliveryMethodId"),
            propIf(p => p.paymentMethodId === PaymentMethodId.Card, () => propRequired("paymentGatewayTransId")),
            propLengthMax("paymentGatewayTransId", 20),
            propValueValidator("paymentMethodId", (p, e) => p === PaymentMethodId.Cod && e.deliveryMethodPlaceId != null &&
                useDeliveryMethods.getState().items.find(d => d.id === e.deliveryMethodId)?.isCodPickupPointAllowed !== true && "COD with pickup point is not allowed."),
            propIf(p => p.deliveryMethodId === DeliveryMethodId.Dpd, () =>
                propValueValidator("weight", (p, e) => {
                    if (p) {
                        const deliveryMethodPlaceId = useOrderStore.getState().clientData.deliveryMethodPlaceId;
                        const deliveryMethod = useDeliveryMethods.getState().items.find(d => d.id === e.deliveryMethodId);

                        const pickUpPointWeightLimit = (deliveryMethod?.pickUpPointWeightLimit ?? 0) * 1000;
                        const homeDeliveryWeightLimit = (deliveryMethod?.homeDeliveryWeightLimit ?? 0) * 1000;

                        if (deliveryMethodPlaceId != null && p > pickUpPointWeightLimit) {
                            return `Maximum order weight limit for pickup point is ${deliveryMethod?.pickUpPointWeightLimit} kg. Consider to split order`;
                        }
                        else if (p > homeDeliveryWeightLimit) {
                            return `Maximum order weight limit for home delivery is ${deliveryMethod?.homeDeliveryWeightLimit} kg. Consider to split order`;
                        }
                    }
                })),
        ]);
    }, [useOrderStore, def]);

    const orderStore = useOrderStore(p => ({
        serverData: p.serverData, clientData$: p.clientData$, entityId: p.entityId, isSaving: p.isSaving, isFetching: p.isFetching,
        isSaveDisabled: p.isSaveDisabled, isFetched: p.isFetched,
        projectId: p.clientData.projectId, detailsError: getMostImportantValidationMessage(p, "details").firstMessage,
        freeArticle: p.clientData.freeArticle, orderStateId: p.clientData.orderStateId, invoiceStateId: p.clientData.invoiceStateId,
        deliveryMethodId: p.clientData.deliveryMethodId, mailId: p.clientData.mailId,
        deliveryMethodPlaceId: p.clientData.deliveryMethodPlaceId,
        paymentMethodId: p.clientData.paymentMethodId,
        isEditing: p.clientData.isEditing,
        callCenterName: p.clientData.callCenterName,
    }));
    const detailsStore = useDetailsStore(p => ({
        selectedItem: p.selectedItem
    }));

    const [useArticleListStore] = useState(() => createListStore<SaleArticleDto>({
        path: "customers/mailOrders/getArticles",
        autoInitialFetch: false,
        onFetched: updateOrder,
    }));
    useRxJsSub(() => useOrderStore.getState().clientData$.pipe(map(p => ({ projectId: p.data.projectId })), distinctUntilChanged(shallow)).subscribe(p => {
        if (p.projectId != null) {
            useArticleListStore.getState().setQs({ projectId: p.projectId });
            useArticleListStore.getState().fetch();
        }
    }));

    var [useMoCondsStore] = useState(() => createListStore<ProjectMailOrderConditionDto>({
        path: "common/projectMailOrderConditions",
        autoInitialFetch: false,
        onFetched: updateOrder,
    }));

    const [useNonMoDetails] = useState(() => createListStore({
        data: combineLatest([
            useArticleListStore.getState().items$.pipe(map(p => ({ items: keyBy(p.items, p => p.id), isFetching: p.isFetching }))),
            useOrderStore.getState().clientData$.pipe(map(p => ({ details: p.data.details, isFetching: p.isFetching })), distinctUntilChanged(shallow))
        ]).pipe(map(p => ({
            items: (p[0].isFetching || p[1].isFetching) ? [] :
                p[1].details.filter(r => p[0].items[r.saleArticleId] == null && !r.isPost),
            isFetching: p[0].isFetching || p[1].isFetching
        })))
    }));
    const nonMoDetails = useNonMoDetails(p => p.items);

    function updateOrder(updateDiscount = false) {
        let orderStore = useOrderStore.getState();
        const disabled = getDisabled(isEditRole, orderStore.clientData.orderStateId, orderStore.clientData.paymentMethodId, isAdminRole);
        const moCondsStore = useMoCondsStore.getState();
        const isMoCondsFetched = disabled || moCondsStore.fetched;
        if (!isMoCondsFetched || !orderStore.isFetched) {
            return;
        }

        orderStore = useOrderStore.getState();
        let items: ShipmentDetailDto[] = [];
        if (disabled || !orderStore.clientData.isEditing) {
            items = orderStore.clientData.details.concat(orderStore.clientData.autos);
        } else {
            let info = getOrderCalculation(orderStore.clientData, { isLoaded: moCondsStore.fetched && !moCondsStore.fetching, items: moCondsStore.items }, project$.value?.maxDiscountAmount, useArticleListStore.getState().items, updateDiscount ? null : orderStore.clientData.discAmt, orderStore.clientData.callCenterId, props.useCustomerStore.getState().clientData.langPref);
            useOrderStore.getState().setClientDataProp("freePostage", info.freePostage);
            useOrderStore.getState().setClientDataProp("autos", info.autos);
            const postageDetails = useOrderStore.getState().clientData.details.filter(p => p.isPost);
            if (postageDetails.length > 0 && info.autos.some(p => p.isPost)) {
                useOrderStore.getState().setClientDataProp("details", useOrderStore.getState().clientData.details.filter(p => !p.isPost));
            }
            items = info.autos.concat(orderStore.clientData.details);
        }

        const postages = items.filter(p => p.isPost);
        const articles = items.filter(p => !p.isPost);
        const postagePrice = Math.round(sum(postages.map(p => p.price * p.quantity)) * 100) / 100;
        const articlePrice = Math.round(sum(articles.map(p => p.price * p.quantity)) * 100) / 100;

        let discAmt = orderStore.clientData.discAmt ?? 0;
        if (updateDiscount && useArticleListStore.getState().fetched) {
            const articlesDict = keyBy(useArticleListStore.getState().items, p => p.id);
            const articlesDiscount = round(sumBy(orderStore.clientData.details.map(p => (articlesDict[p.saleArticleId]?.discounts?.find(d =>
                d.fromQuantity <= p.quantity && d.toQuantity >= p.quantity)?.amount ?? 0) * p.quantity / p.baseQuantity)), 2);
            const projectDiscount = useProjectsStore.getState().items.find(p => p.id === orderStore.clientData.projectId)?.maxDiscountAmount ?? 0;
            discAmt = Math.min(articlePrice, Math.max(articlesDiscount, projectDiscount));
            useOrderStore.getState().setClientDataProp("discAmt", discAmt);
            useOrderStore.getState().setClientDataProp("discAmtFormatted", undefined);
        }

        const orderAmount = round(articlePrice - discAmt, 2);
        const totalCurrentWeight = round(sum(items.map(p => p.weight * p.quantity)));
        useOrderStore.getState().setClientDataProp("postagePrice", postagePrice);
        useOrderStore.getState().setClientDataProp("articlePrice", articlePrice);
        useOrderStore.getState().setClientDataProp("orderAmount", round(orderAmount, 2));
        useOrderStore.getState().setClientDataProp("toPay", round(orderAmount + postagePrice, 2));
        useOrderStore.getState().setClientDataProp("weight", totalCurrentWeight);

        const weightIsDefined = deliveryMethod?.homeDeliveryWeightLimit || deliveryMethod?.pickUpPointWeightLimit;

        if (weightIsDefined) {
            const deliveryMethodPlaceId = useOrderStore.getState().clientData.deliveryMethodPlaceId;

            const pickUpPointWeightLimit = (deliveryMethod?.pickUpPointWeightLimit ?? 0) * 1000;
            const homeDeliveryWeightLimit = (deliveryMethod?.homeDeliveryWeightLimit ?? 0) * 1000;

            if (deliveryMethodPlaceId != null && totalCurrentWeight > pickUpPointWeightLimit)
                simpleDialog.show("Order overweight warning", `Maximum order weight limit for pickup point is ${deliveryMethod?.pickUpPointWeightLimit} kg. Consider to split order`);
            else if (totalCurrentWeight > homeDeliveryWeightLimit)
                simpleDialog.show("Order overweight warning", `Maximum order weight limit for home delivery is ${deliveryMethod?.homeDeliveryWeightLimit} kg. Consider to split order`);
        }
    }

    function addArticle(quantity: number, article: SaleArticleDto) {
        let details = useOrderStore.getState().clientData.details;
        const existingDetail = details.find(p => p.saleArticleId === article.id);
        function getNewArticle(extraQuantity: number): ShipmentDetailDto {
            return {
                id: newId(),
                saleArticleId: article.id,
                code: article.code,
                name: article.name,
                price: article.price,
                isPost: article.isPost,
                baseQuantity: article.baseQuantity,
                quantity: quantity + extraQuantity,
                weight: article.weight,
                isAuto: false,
                packageType: article.packageType,
                isAvailable: article.isAvailable,
            };
        }
        if (existingDetail != null) {
            details = details.map(p => p !== existingDetail ? p : getNewArticle(p.quantity));
        } else {
            details = details.concat(getNewArticle(0));
        }
        useOrderStore.getState().setClientDataProp("details", details);
        useOrderStore.getState().setClientDataProp("isEditing", true);
        updateOrder(true);
    }

    function updateArticle(quantity: number, article: SaleArticleDto) {
        let details = useOrderStore.getState().clientData.details;
        const existingDetail = details.find(p => p.saleArticleId === article.id);
        details = details.map(p => p !== existingDetail ? p : {
            id: p.id,
            saleArticleId: article.id,
            code: article.code,
            name: article.name,
            price: article.price,
            isPost: article.isPost,
            baseQuantity: article.baseQuantity,
            quantity,
            weight: article.weight,
            articlePrice: article.price,
            isAuto: false,
            packageType: article.packageType,
            isAvailable: article.isAvailable,
        });
        useOrderStore.getState().setClientDataProp("details", details);
        useOrderStore.getState().setClientDataProp("isEditing", true);
        updateOrder(true);
    }

    const disabled = getDisabled(isEditRole, orderStore.orderStateId, orderStore.paymentMethodId, isAdminRole);
    const isShipped = orderStore.serverData.orderStateId === OrderStateId.Shipped;
    const canCopyOrder = isEditRole && isShipped;
    const projectChangeDisabled = disabled || orderStore.entityId != null || orderStore.isSaving || props.newMoData != null;
    const canChangeSelectedDetail = isEditRole && detailsStore.selectedItem != null &&
        detailsStore.selectedItem.isPost === false;
    const canRemoveSelectedDetail = isEditRole && detailsStore.selectedItem != null &&
        (detailsStore.selectedItem.isPost === false || orderStore.entityId == null);

    useEffect(() => {
        if (!disabled && orderStore.projectId != null) {
            useMoCondsStore.getState().setQs({
                projectId: orderStore.projectId,
            });
            useMoCondsStore.getState().fetch();
        }
    }, [disabled, orderStore.projectId, useMoCondsStore]);

    const editDetailDialog = useDialog(close => <OrderShipmentDetailDialog close={close} onChange={updateArticle}
        editingSaleArticle={{
            id: detailsStore.selectedItem!.saleArticleId,
            quantity: detailsStore.selectedItem!.quantity,
            baseQuantity: detailsStore.selectedItem!.baseQuantity,
            isAvailable: detailsStore.selectedItem!.isAvailable,
        }}
        title={() => customerOrderDialogTitle(orderStore.serverData.ordId, false, () => "Edit MO article", customer)}
        useArticleListStore={useArticleListStore} isArticleInDetails={p => useOrderStore.getState().clientData.details.some(r => r.saleArticleId === p)}
    />, "sm");

    const newDetailDialog = useDialog(close => <OrderShipmentDetailDialog close={close} onChange={addArticle}
        title={() => customerOrderDialogTitle(orderStore.serverData.ordId, false, () => "New MO article", customer)}
        useArticleListStore={useArticleListStore} isArticleInDetails={p => useOrderStore.getState().clientData.details.some(r => r.saleArticleId === p)}
    />, "sm");

    const removeDetailDialog = useDialog(close => <>
        <DialogTitle>Remove article</DialogTitle>
        <DialogContent>Remove article from MO?</DialogContent>
        <DialogActions>
            <Button onClick={() => {
                close();
                const orderStore = useOrderStore.getState();
                orderStore.setClientDataProp("details", orderStore.clientData.details.filter(p => p.saleArticleId !== detailsStore.selectedItem!.saleArticleId));
                orderStore.setClientDataProp("isEditing", true);
                updateOrder(true);
            }} color="positive">Yes</Button>
            <Button onClick={close} color="negative">No</Button>
        </DialogActions>
    </>, null);

    function onProductChange() {
        useOrderStore.getState().setClientDataProp("projectId", null);
        useOrderStore.getState().setClientDataProp("details", []);
        updateOrder(true);
    }

    function onProjectChange(projectId: number | null) {
        useOrderStore.getState().setClientDataProp("details", []);
        updateOrder(true);
    }

    function editSelectedArticle() {
        if (canChangeSelectedDetail && !disabled) {
            editDetailDialog.show();
        }
    }

    function removeSelectedArticle() {
        removeDetailDialog.show();
    }

    const orderStates = useOrderStates(p => p.items);
    const orderState = getOrderState(orderStore, orderStates);

    function makeOrderActive() {
        const store = useOrderStore.getState();
        store.setClientDataProp("orderStateId", OrderStateId.Active);
    }

    function makeOrderPassive() {
        const store = useOrderStore.getState();
        store.setClientDataProp("orderStateId", OrderStateId.Passive);
    }

    const deliveryPlaceDialog = useDialog((close, dialogProps) => <DeliveryPlaceDialog close={close} dialogProps={dialogProps} orderId={props.orderId}
        deliveryMethodId={orderStore.deliveryMethodId!} deliveryMethodPlaceId={orderStore.deliveryMethodPlaceId ?? null}
        changeDeliveryMethodPlace={(id, name, isCoDHandler) => {
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceId", id);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceName", name);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceIsCoDHandler", isCoDHandler);
            updateOrder();
        }}
        getCustomerAddress={() => {
            const customer = props.useCustomerStore.getState().serverData;
            const order = useOrderStore.getState().clientData;
            return order.deliveryAddressId == null ? customer :
                customer.customerAddresses.find(p => p.id === order.deliveryAddressId)!;
        }} />, undefined, { usingCustomDialogTag: true });
    async function showDeliveryMethodPlaceDialog() {
        if (useOrderStore.getState().clientData.deliveryMethodId != null) {
            deliveryPlaceDialog.show();
        }
    }

    const deliveryMethods = useDeliveryMethods(p => p.items);
    const deliveryMethod = useMemo(() => deliveryMethods.find(p => p.id === orderStore.deliveryMethodId), [deliveryMethods, orderStore.deliveryMethodId]);

    // same logic is in TM and CO
    useRxJsSub(() => combineLatest([
        useProjectsStore.getState().items$,
        useOrderStore.getState().clientData$.pipe(map(p => p.data.projectId), distinctUntilChanged(), filter(p => p != null))
    ]).subscribe(([projects, projectId]) => {
        const deliveryMethodId = projects.items.find(p => p.id === projectId)?.deliveryMethodId ?? null;
        if (deliveryMethodId != null && deliveryMethodId !== useOrderStore.getState().clientData.deliveryMethodId) {
            useOrderStore.getState().setServerDataProp("deliveryMethodId", deliveryMethodId);
            useOrderStore.getState().setClientDataProp("deliveryMethodId", deliveryMethodId);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceId", null);
            useOrderStore.getState().setClientDataProp("deliveryMethodPlaceName", null);
        }

        const paymentMethodId = projects.items.find(p => p.id === projectId)?.paymentMethodId ?? null;
        if (paymentMethodId != null && paymentMethodId !== useOrderStore.getState().clientData.paymentMethodId && props.orderId == null) {
            useOrderStore.getState().setServerDataProp("paymentMethodId", paymentMethodId);
            useOrderStore.getState().setClientDataProp("paymentMethodId", paymentMethodId);
        }
    }));

    const [project$] = useState(() => new BehaviorSubject<OrderProjectDto | undefined>(undefined));
    useRxJsSub(() => combineLatest([
        useProjectsStore.getState().items$,
        useOrderStore.getState().clientData$.pipe(map(p => p.data.projectId), distinctUntilChanged())
    ]).pipe(map(([projects, p]) => {
        return projects.items.find(r => r.id === p);
    })).subscribe(project$));
    const [useProjectPaymentMethods] = useState(() => createListStore<EnumItemDto>({
        data: combineLatest([
            project$,
            usePaymentMethods.getState().items$,
        ]).pipe(map(([project, paymentMethods]) => {
            return {
                items: project == null ? [] : project.paymentMethodIds.length === 0 ? paymentMethods.items :
                    paymentMethods.items.map(p => ({ ...p, inactive: p.inactive || !project.paymentMethodIds.includes(p.id as PaymentMethodId) })),
                isFetching: paymentMethods.isFetching,
            };
        })),
    }));

    function copy() {
        props.close();
        const order = useOrderStore.getState().serverData;
        props.setNewMoData!(order);
    }

    const weight = useOrderStore(p => p.clientData.weight);

    const { addressListDialog, useCustomerAddressListStore } = useAddressList(props.useCustomerStore, disabled);

    const simpleDialog = useSimpleDialog();

    function onFreePostageChange(newValue: boolean) {
        const order = useOrderStore.getState().clientData;
        const postage = order.details.concat(order.autos).find(p => p.isPost);
        if (postage == null) {
            simpleDialog.show("Free postage", "Postage&Handling is missing.");
            return;
        }
        if (!newValue && postage.origPrice === 0) {
            useOrderStore.getState().setClientDataProp("freePostage", true);
            simpleDialog.show("Free postage", "Postage&Handling fee is already 0 and cannot be changed.");
        } else {
            useOrderStore.getState().setClientDataProp("details", useOrderStore.getState().clientData.details.map(p =>
                p.isPost && (p.price === 0) !== newValue && p.origPrice != null ? { ...p, price: newValue ? 0 : p.origPrice } : p));
            useOrderStore.getState().setClientDataProp("isEditing", true);
            updateOrder();
        }
    }

    function onDiscountChange() {
        useOrderStore.getState().setClientDataProp("isEditing", true);
        updateOrder();
    }

    return <>
        {editDetailDialog.render()}
        {newDetailDialog.render()}
        {removeDetailDialog.render()}
        {deliveryPlaceDialog.render()}
        {addressListDialog.render()}
        {simpleDialog.render()}
        <DialogTitle>{customerOrderDialogTitle(orderStore.serverData.ordId, orderStore.isFetching, isNew => isNew ? "New MO" : "Edit MO", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={4} alignItems="stretch">
                <Grid container item xs={4} columnSpacing={2} alignSelf="flex-start">
                    {!orderStore.isEditing && (orderStore.entityId != null || props.newMoData != null) && !disabled && <Grid item xs={12} mb={2}><Button onClick={() => {
                        useOrderStore.getState().setClientDataProp("isEditing", true);
                        updateOrder();
                    }} fullWidth>Start editing this order</Button></Grid>}
                    <Grid item xs={12}>{propEnumField(useOrderStore)("productId", "Product", useProductsStore, { onChange: onProductChange, disabled: true })}</Grid>
                    <Grid item xs={12}>{propEnumField(useOrderStore)("projectId", "Project", useProjectsStore, { onChange: onProjectChange, disabled: projectChangeDisabled })}</Grid>
                    <Grid item xs={6}>{propTextField(useOrderStore)("sc", "Media code", { disabled: disabled || props.newMoData != null })}</Grid>
                    <Grid item xs={6}><TextField value={orderStore.isFetched ? (orderStore.mailId ?? def.clientData.mailId).toString() : ""} label="Mail ID" disabled /></Grid>
                    {orderStore.callCenterName &&
                        <>
                            <Grid item xs={12}>{propTextField(useOrderStore)("callCenterName", "Call center", { disabled: true })}</Grid>
                            {def.clientData.isTmOrderConfirmationRequired &&
                                <>
                                    <Grid item xs={6} m={0} p={0}>{propBoolField(useOrderStore)("confirmedByCustomer", "Confirmed by customer", {
                                        disabled,
                                        sx: { margin: "1px 0 1px 0" }
                                    })}</Grid>
                                    <Grid item xs={6} m={0} p={0}>{propBoolField(useOrderStore)("affidavit", "Honour Declaration", {
                                        disabled: true,
                                        sx: { margin: "1px 0 1px 0" }
                                    })}</Grid>
                                </>}
                        </>
                    }
                    <Grid item xs={12}>{propEnumField(useOrderStore)("paymentMethodId", "Payment method", useProjectPaymentMethods, { disabled })}</Grid>
                    {orderStore.paymentMethodId === PaymentMethodId.Card &&
                        <Grid item xs={12}>{propTextField(useOrderStore)("paymentGatewayTransId", "Card payment code", { disabled })}</Grid>}
                    {deliveryMethod?.hasPlaces && <Grid item container xs={12} spacing={2}>
                        <Grid item xs>{propTextField(useOrderStore)("deliveryMethodPlaceName", `${deliveryMethod?.name} parcel shop`, { readOnly: true, disabled })}</Grid>
                        <Grid item><Button disabled={disabled || deliveryMethod == null ? true : !deliveryMethod.hasPlaces} sx={InputSizedButtonSx} onClick={showDeliveryMethodPlaceDialog}>Change</Button></Grid>
                    </Grid>}
                    <Grid item container xs={12} spacing={2}>
                        <Grid item xs zeroMinWidth>{propEnumField(useOrderStore)("deliveryAddressId", "Customer's delivery address", useCustomerAddressListStore,
                            { disabled, autoComplete: false, addNone: true })}</Grid>
                        <Grid item><Button disabled={disabled} sx={InputSizedButtonSx} onClick={() => addressListDialog.show()}>Manage</Button></Grid>
                    </Grid>
                    <Grid item xs={6}>{propDateField(useOrderStore)("ordDte", null, "Enrolment date", { disabled: true })}</Grid>
                    <Grid item xs={6}>{propDateField(useOrderStore)("minShpDte", "minShpDteFormatted", "Min. SHP date", { disabled })}</Grid>
                    <Grid item xs={6}>{propNumberField(useOrderStore)("articlePrice", null, "Article price", { isCurrency: true, disabled: true })}</Grid>
                    <Grid item xs={6}>{propNumberField(useOrderStore)("discAmt", "discAmtFormatted", "Discount", { isCurrency: true, disabled, onChange: onDiscountChange })}</Grid>
                    <Grid item xs={6}>{propNumberField(useOrderStore)("orderAmount", null, "Order amount", { isCurrency: true, disabled: true })}</Grid>
                    <Grid item xs={6}>{propNumberField(useOrderStore)("postagePrice", null, "Postage", { isCurrency: true, disabled: true })}</Grid>
                    <Grid item xs={6}>{propNumberField(useOrderStore)("toPay", null, "To pay", { isCurrency: true, disabled: true })}</Grid>
                    <Grid item xs={6}>{propNumberField(useOrderStore)("weight", null, "Weight", {
                        disabled: true,
                        unit: weight == null ? undefined : weight >= 1000 ? "kg" : "g",
                        transformValue: p => weight != null && p != null && weight >= 1000 ? p / 1000 : p,
                    })}</Grid>
                    <Grid item xs={12}><Stack direction="row" spacing={2} alignItems="center">
                        <TextField value={orderState} label="Order state" disabled />
                        <Button sx={InputSizedButtonSx} title="Make order active" onClick={makeOrderActive}
                            disabled={disabled || orderStore.orderStateId !== OrderStateId.Passive || orderStore.isFetching} color="positive">A</Button>
                        <Button sx={InputSizedButtonSx} title="Make order passive" onClick={makeOrderPassive}
                            disabled={disabled || orderStore.orderStateId !== OrderStateId.Active || orderStore.isFetching} color="negative">P</Button>
                    </Stack></Grid>
                </Grid>
                <Grid container item xs columnSpacing={2} alignSelf="flex-start">
                    <Grid item xs={12}>
                        {!isShipped && nonMoDetails.length > 0 && <Alert severity="error">These articles are not available anymore: {nonMoDetails.map(p => p.code).join(", ")}.</Alert>}
                        <Typography variant="h6">Articles</Typography>
                        {renderListStore(useDetailsStore, {
                            header: listHeaderItems([
                                ["edit", () => <Button color="edit" title="Edit" disabled={!canChangeSelectedDetail || disabled} onClick={editSelectedArticle}><Edit /></Button>],
                                ["add", () => <Button color="add" title="Add" disabled={orderStore.projectId == null || disabled} onClick={() => newDetailDialog.show()}><Add /></Button>],
                                ["remove", () => <Button color="delete" disabled={!canRemoveSelectedDetail || disabled} title="Remove" onClick={removeSelectedArticle}><Remove /></Button>],
                            ]), onDoubleClick: editSelectedArticle,
                            hideFooter: true, rowHeight: 30,
                            getRowClass: item => !isShipped && (nonMoDetails.some(p => p.saleArticleId === item.saleArticleId) || !item.isAvailable) && ["redRibbon"],
                        })}
                        {orderStore.detailsError && <FormHelperText error>{orderStore.detailsError}</FormHelperText>}
                    </Grid>
                    <Grid item xs={6} mt={1}>
                        <TextField disabled label="Free article" value={orderStore.freeArticle == null ? "" :
                            `${orderStore.freeArticle.articleCode} ${orderStore.freeArticle.articleName}` +
                            ` (${formatNumber(orderStore.freeArticle.btoPrc, def.clientData.landCode, true)})`} />
                    </Grid>
                    <Grid item xs={6} mt={1}>
                        <TextField disabled label="In product" value={orderStore.freeArticle == null ? "" :
                            `${orderStore.freeArticle.productCode} ${orderStore.freeArticle.productName}`} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Auto articles</Typography>
                        {renderListStore(useAutosStore, {
                            hideFooter: true,
                            rowHeight: 30,
                            getRowClass: item => !isShipped && !item.isAvailable && ["redRibbon"],
                        })}
                    </Grid>
                    <Grid item xs={12}>{propBoolField(useOrderStore)("freePostage", "Free postage", {
                        disabled: disabled,
                        onChange: onFreePostageChange,
                    })}</Grid>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            {isEditRole && props.setNewMoData && <Button disabled={!orderStore.isFetched || !canCopyOrder} onClick={copy}>Copy</Button>}
            <Button disabled={orderStore.isSaveDisabled || disabled || (nonMoDetails.length > 0 && orderStore.orderStateId !== OrderStateId.Passive)} onClick={useOrderStore.getState().save} color="save">Save</Button>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

function getDisabled(isEditRole: boolean, orderStateId: OrderStateId | null | undefined, paymentMethodId: number | null | undefined, isAdminRole: boolean) {
    return !isEditRole || !isOrderEditable(orderStateId, paymentMethodId, isAdminRole);
}